import {
  chunk,
  deduplicateById,
  displayCodeableConcept,
  displayCodeableConcepts,
  referenceKey,
} from "./fhir.utils";
import {
  AppointmentResource,
  FhirBundle,
  FhirClientPort,
  SlotResource,
} from "./fhir.types";

export interface AppointmentModel {
  id: string;
  title: string;
  description?: string;
  start: string;
  end: string;
  status: string;
  slotReferences: string[];
  participantReferences: string[];
  participantDisplays: string[];
  locationDisplay?: string;
  comment?: string;
  patientInstruction?: string;
}

function mapAppointment(
  resource: AppointmentResource,
): AppointmentModel | undefined {
  if (!resource.id || !resource.start || !resource.end) {
    return undefined;
  }

  const serviceLabels = displayCodeableConcepts(resource.serviceType);
  const title =
    resource.description?.trim() ||
    displayCodeableConcept(resource.appointmentType) ||
    serviceLabels[0] ||
    `Appointment/${resource.id}`;

  const participantReferences = Array.from(
    new Set(
      (resource.participant ?? [])
        .map((participant) => referenceKey(participant.actor?.reference))
        .filter((reference): reference is string => Boolean(reference)),
    ),
  );
  const participantDisplays = Array.from(
    new Set(
      (resource.participant ?? [])
        .map((participant) => participant.actor?.display?.trim())
        .filter((display): display is string => Boolean(display)),
    ),
  );

  const locationDisplay = (resource.participant ?? [])
    .find((participant) =>
      referenceKey(participant.actor?.reference)?.startsWith("Location/"),
    )
    ?.actor?.display?.trim();

  return {
    id: resource.id,
    title,
    description:
      resource.description?.trim() && resource.description.trim() !== title
        ? resource.description.trim()
        : undefined,
    start: resource.start,
    end: resource.end,
    status: resource.status ?? "unknown",
    slotReferences: (resource.slot ?? [])
      .map((slot) => referenceKey(slot.reference))
      .filter((reference): reference is string => Boolean(reference)),
    participantReferences,
    participantDisplays,
    locationDisplay,
    comment: resource.comment?.trim() || undefined,
    patientInstruction: resource.patientInstruction?.trim() || undefined,
  };
}

function appointmentsFromBundles(
  bundles: Awaited<ReturnType<FhirClientPort["search"]>>[],
): AppointmentModel[] {
  const appointments = bundles.flatMap((bundle) =>
    (bundle.entry ?? [])
      .map((entry) => entry.resource)
      .filter(
        (resource): resource is AppointmentResource =>
          resource?.resourceType === "Appointment",
      )
      .map(mapAppointment)
      .filter(
        (appointment): appointment is AppointmentModel => Boolean(appointment),
      ),
  );

  return deduplicateById(appointments);
}

export class AppointmentService {
  constructor(private readonly client: FhirClientPort) {}

  async searchBySlots(slotIds: string[]): Promise<AppointmentModel[]> {
    if (slotIds.length === 0) {
      return [];
    }

    const bundles = await Promise.all(
      chunk(slotIds, 20).map((ids) =>
        this.client.search("Appointment", {
          slot: ids.map((id) => `Slot/${id}`).join(","),
          _count: 500,
        }),
      ),
    );

    return appointmentsFromBundles(bundles);
  }

  async searchByActors(
    actorReferences: string[],
    start: string,
    end: string,
  ): Promise<AppointmentModel[]> {
    if (actorReferences.length === 0) {
      return [];
    }

    const bundles = await Promise.all(
      chunk(actorReferences, 20).map((references) =>
        this.client.search("Appointment", {
          actor: references.join(","),
          date: [`ge${start}`, `lt${end}`],
          _count: 500,
        }),
      ),
    );

    return appointmentsFromBundles(bundles);
  }

  async createWithReservedSlots(
    appointment: AppointmentResource,
    slotResources: SlotResource[],
  ): Promise<FhirBundle | AppointmentResource> {
    const uniqueSlots = Array.from(
      new Map(slotResources.filter((slot) => slot.id).map((slot) => [slot.id!, slot])).values(),
    );

    if (uniqueSlots.length === 0) {
      return (await this.client.create("Appointment", appointment)) as AppointmentResource;
    }

    const transaction: FhirBundle = {
      resourceType: "Bundle",
      type: "transaction",
      entry: [
        {
          fullUrl: `urn:uuid:${crypto.randomUUID()}`,
          resource: appointment,
          request: { method: "POST", url: "Appointment" },
        },
        ...uniqueSlots.map((slot) => ({
          resource: { ...slot, status: "busy" },
          request: {
            method: "PUT" as const,
            url: `Slot/${slot.id}`,
            ...(slot.meta?.versionId
              ? { ifMatch: `W/"${slot.meta.versionId}"` }
              : {}),
          },
        })),
      ],
    };

    return this.client.transaction(transaction);
  }
}
